import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authLink } from '../utils/authReturnPath';
import { Shield, Bell, Calculator, TrendingUp, Globe, Phone, HelpCircle, Info, Menu, X, User, LogOut, CreditCard, ChevronDown, LayoutDashboard, Sparkles, Newspaper, Home, FileSearch, Building2, LockKeyhole, ClipboardList } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import BrandLogo from './BrandLogo';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import { useProcurementAccess } from '../context/ProcurementAccessContext';

export default function Navigation({ children }: { children?: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, toggleLanguage, language } = useLanguage();
  const { user, subscription, logout } = useAuth();
  const { allowed, unreadCount } = useProcurementAccess();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isSidebarFocused, setIsSidebarFocused] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isBalanceOpen, setIsBalanceOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const balanceRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const sidebarExpanded = isHovered || isSidebarFocused || isMobileOpen;

  const isAuth = !!(localStorage.getItem('access_token') || sessionStorage.getItem('access_token'));
  const canAccessAdmin = !!user && ['superadmin', 'manager', 'analyst'].includes(user.role);
  const publicSurface = ['/', '/plans', '/about', '/contacts', '/questions'].includes(location.pathname) || location.pathname.startsWith('/services/') || location.pathname.startsWith('/news');
  const informationLinkActive = (path: string) => location.pathname === path || (path === '/news' && location.pathname.startsWith('/news/'));
  const publicLinks = [
    { name: language === 'kk' ? 'Мүмкіндіктер' : 'Возможности', path: '/#search' },
    { name: language === 'kk' ? 'Қалай жұмыс істейді' : 'Как это работает', path: '/#notifications' },
    { name: t('nav.plans'), path: '/plans' },
    { name: t('nav.news'), path: '/news' },
    { name: t('nav.contacts'), path: '/contacts' },
  ];

  const userInitials = user ? ((user.name?.charAt(0) || '') + (user.surname?.charAt(0) || '')).toUpperCase() || 'U' : 'U';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (balanceRef.current && !balanceRef.current.contains(event.target as Node)) {
        setIsBalanceOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsProfileMenuOpen(false);
    setIsBalanceOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileOpen(false);
        setIsProfileMenuOpen(false);
        setIsBalanceOpen(false);
        if (isMobileOpen) mobileToggleRef.current?.focus();
      }
      if (event.key === 'Tab' && isMobileOpen) {
        const controls = Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>('a[href], button') || [])
          .filter(control => control.getClientRects().length > 0);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    if (isMobileOpen) mobileCloseRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobileOpen]);

  useEffect(() => {
    // A drawer opened on mobile must not remain modal after rotating/resizing.
    const breakpoint = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (breakpoint.matches) {
        setIsMobileOpen(false);
        setIsSidebarFocused(false);
        setIsHovered(false);
        if (document.activeElement === mobileCloseRef.current) mobileCloseRef.current?.blur();
      }
    };
    breakpoint.addEventListener('change', onChange);
    return () => breakpoint.removeEventListener('change', onChange);
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsProfileMenuOpen(false);
    navigate('/login');
  };

  const mainLinks: { name: string; path: string; icon: React.ReactNode; locked?: boolean; count?: number }[] = [
    { name: t('nav.main'), path: '/', icon: <Shield className="w-6 h-6 flex-shrink-0" /> },
    ...[
      { name: t('nav.tracker'), path: '/tracker', icon: <Bell className="w-6 h-6 flex-shrink-0" /> },
      { name: t('nav.calculator'), path: '/calculator', icon: <Calculator className="w-6 h-6 flex-shrink-0" /> },
      { name: t('nav.financialPlanning'), path: '/financial-planning', icon: <TrendingUp className="w-6 h-6 flex-shrink-0" /> },
      { name: t('nav.experience'), path: '/experience', icon: <ClipboardList className="w-6 h-6 flex-shrink-0" /> },
      { name: t('nav.lots'), path: '/lots', icon: <FileSearch className="w-6 h-6 flex-shrink-0" />, locked: isAuth && !allowed('lots'), count: unreadCount ?? undefined },
      { name: t('nav.suppliers'), path: '/suppliers', icon: <Building2 className="w-6 h-6 flex-shrink-0" />, locked: isAuth && !allowed('suppliers') },
      ...(canAccessAdmin ? [
        { name: t('nav.adminPanel'), path: '/admin', icon: <LayoutDashboard className="w-6 h-6 flex-shrink-0" /> },
      ] : []),
    ]
  ];

  const infoLinks = [
    { name: t('nav.main'), path: '/', icon: <Home className="w-5 h-5 flex-shrink-0" />, desktopOnly: true },
    { name: t('nav.news'), path: '/news', icon: <Newspaper className="w-5 h-5 flex-shrink-0" />, desktopOnly: false },
    { name: t('nav.contacts'), path: '/contacts', icon: <Phone className="w-5 h-5 flex-shrink-0" />, desktopOnly: false },
    { name: t('nav.questions'), path: '/questions', icon: <HelpCircle className="w-5 h-5 flex-shrink-0" />, desktopOnly: false },
    { name: t('nav.about'), path: '/about', icon: <Info className="w-5 h-5 flex-shrink-0" />, desktopOnly: false },
  ];

  return (
    <div className="app-shell flex bg-slate-50 overflow-hidden font-sans">
      
      {/* Left Sidebar Fixed Wrapper */}
      <aside 
        id="service-navigation"
        ref={sidebarRef}
        aria-label={t('nav.services')}
        role={isMobileOpen ? 'dialog' : undefined}
        aria-modal={isMobileOpen || undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsSidebarFocused(true)}
        onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsSidebarFocused(false); }}
        className={`service-navigation fixed z-50 flex-col bg-[#183b50] text-white transition-all duration-200 ease-in-out
        ${isMobileOpen ? 'flex inset-y-0 left-0 rounded-none' : 'hidden lg:flex'}
        lg:top-1/2 lg:-translate-y-1/2 lg:left-0 lg:h-auto lg:rounded-r-2xl lg:py-4
        ${sidebarExpanded ? 'w-80 max-w-full' : 'lg:w-20 lg:items-center'}`}
      >
        <button ref={mobileCloseRef} type="button" onClick={() => { setIsMobileOpen(false); mobileToggleRef.current?.focus(); }}
          aria-label={t('nav.closeMenu')} className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white/10 lg:hidden">
          <X className="h-5 w-5" />
        </button>
        {/* Sidebar Header (Logo) */}
        <div className={`flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isMobileOpen ? 'h-16 border-b border-white/10 px-4 mt-10 mb-2' : 'mb-4'}`}>
          <Link to="/" aria-label="G-Launcher" className="flex items-center justify-center gap-3 text-white hover:text-goszakup-100 transition-colors overflow-hidden" onClick={() => setIsMobileOpen(false)}>
            <BrandLogo variant="mark" alt="" className="h-12 w-12 shrink-0 rounded-xl bg-white p-1.5" />
            <span className={`font-extrabold text-2xl tracking-tight whitespace-nowrap transition-all duration-300 ${!sidebarExpanded ? 'w-0 opacity-0 hidden' : 'w-auto opacity-100'}`}>
              G-Launcher
            </span>
          </Link>
        </div>

        {/* Vertically Aligned Main Links Area with Upper Spacing */}
        <div className={`flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain ${isMobileOpen ? 'px-3 pb-4 flex-1' : 'px-3'}`}>
          {mainLinks.map((link) => {
            const isActive = ['/admin', '/lots', '/suppliers'].includes(link.path)
              ? location.pathname.startsWith(link.path)
              : location.pathname === link.path;
            const isCollapsed = !sidebarExpanded;
            return (
              <Link 
                key={link.path} 
                to={link.path} 
                onClick={() => setIsMobileOpen(false)}
                title={isCollapsed ? link.name : ""}
                aria-label={link.name}
                aria-current={isActive ? 'page' : undefined}
                className={`flex items-center w-full rounded-xl font-bold transition-colors duration-200 overflow-hidden
                ${isActive ? 'bg-white text-goszakup-700' : 'text-white/80 hover:bg-white/10 hover:text-white'}
                ${isCollapsed ? 'justify-center p-3' : 'justify-start px-4 py-3 gap-3'}`}
              >
                {link.icon}
                {!isCollapsed && (
                  <span className="min-w-0 text-sm leading-snug">
                    {link.name}
                    {link.locked && <LockKeyhole aria-label={t('procurementAdmin.noAccess')} className="inline ml-2 w-4 h-4 opacity-70" />}
                    {!!link.count && <span className="inline-block ml-2 rounded-full bg-amber-300 text-amber-950 px-2 text-xs">{link.count > 99 ? '99+' : link.count}</span>}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Subscription CTA — visually distinct from project services */}
          {(() => {
            const isCollapsed = !sidebarExpanded;
            const isActive = location.pathname === '/plans';
            return (
              <div className={`${isCollapsed ? '' : 'pt-2 mt-1 border-t border-white/10'}`}>
                <Link
                  to="/plans"
                  onClick={() => setIsMobileOpen(false)}
                  title={isCollapsed ? t('nav.plans') : ''}
                  aria-label={t('nav.plans')}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex items-center w-full rounded-xl font-bold transition-all duration-200 overflow-hidden
                  ${isActive
                    ? 'bg-white text-goszakup-700'
                    : 'bg-white/5 text-white/80 ring-1 ring-white/20 hover:bg-white/10 hover:text-white'}
                  ${isCollapsed ? 'justify-center p-3' : 'justify-start px-4 py-3 gap-3'}`}
                >
                  <CreditCard className="w-6 h-6 flex-shrink-0" />
                  {!isCollapsed && (
                    <span className="min-w-0 text-sm leading-snug flex items-center gap-2">
                      {t('nav.plans')}
                    </span>
                  )}
                </Link>
              </div>
            );
          })()}
          <nav aria-label={t('nav.information')} className="mt-2 border-t border-white/20 pt-2 lg:hidden">
            {infoLinks.filter(link => !link.desktopOnly).map(link => (
              <Link key={link.path} to={link.path} onClick={() => setIsMobileOpen(false)}
                aria-current={informationLinkActive(link.path) ? 'page' : undefined}
                className={`flex min-h-[44px] items-center gap-3 rounded-xl px-4 py-2 text-sm font-medium ${informationLinkActive(link.path) ? 'bg-white/15 text-white' : 'text-white/90 hover:bg-white/10'}`}>
                {link.icon}<span>{link.name}</span>
              </Link>
            ))}
          </nav>
        </div>
      </aside>

      {/* Dimmed Overlay when Mobile Sidebar is Open */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-opacity" onClick={() => { setIsMobileOpen(false); mobileToggleRef.current?.focus(); }} />
      )}

      {/* Main Window Wrapper */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 transition-all duration-300 w-full relative">
        
        {/* Top Navbar */}
        <header className="site-header relative h-16 shrink-0 bg-white border-b border-gray-100 flex items-center justify-between gap-2 px-2 sm:px-4 lg:pl-[6.5rem] z-30 w-full">

          <div className="flex shrink-0 items-center gap-2">
            <button ref={mobileToggleRef} type="button" onClick={() => { setIsMobileOpen(true); setIsProfileMenuOpen(false); setIsBalanceOpen(false); }} aria-label={t('nav.openMenu')}
              aria-expanded={isMobileOpen} aria-controls="service-navigation" className="lg:hidden flex h-11 w-11 items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/" aria-label="G-Launcher" className="flex items-center gap-2 text-goszakup-600 hover:text-goszakup-700 transition-colors">
              <BrandLogo variant="mark" alt="" className="h-8 w-8 shrink-0" />
              <span className="hidden xl:inline font-extrabold text-lg tracking-tight">G-Launcher</span>
            </Link>
          </div>

          <Link to="/news" title={t('nav.news')} aria-label={t('nav.news')}
            aria-current={informationLinkActive('/news') ? 'page' : undefined}
            className={`${publicSurface ? 'lg:hidden' : 'md:hidden'} inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-lg px-2.5 text-sm font-medium transition-colors ${informationLinkActive('/news') ? 'bg-goszakup-50 text-goszakup-700' : 'text-slate-600 hover:bg-slate-50 hover:text-goszakup-700'}`}>
            <Newspaper className="h-5 w-5 shrink-0" /><span className="hidden sm:inline">{t('nav.news')}</span>
          </Link>

          <nav aria-label={t('nav.information')} className={`hidden min-w-0 flex-1 items-center justify-center gap-1 ${publicSurface ? 'lg:flex' : 'md:flex'}`}>
            {publicSurface ? publicLinks.map(link => <a key={link.path} href={link.path}
              className={`inline-flex min-h-[44px] items-center whitespace-nowrap rounded-lg px-2 text-sm font-medium transition-colors xl:px-3 ${informationLinkActive(link.path) ? 'bg-goszakup-50 text-goszakup-700' : 'text-slate-600 hover:bg-slate-50 hover:text-goszakup-700'}`}
              aria-current={informationLinkActive(link.path) ? 'page' : undefined}>{link.name}</a>) : infoLinks.map((link) => {
              const isActive = informationLinkActive(link.path);
              return (
                <Link 
                  key={link.path} 
                  to={link.path} 
                  title={link.name}
                  aria-label={link.name}
                  aria-current={isActive ? 'page' : undefined}
                  className={`${link.desktopOnly ? 'hidden lg:flex' : 'flex'} min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 px-2 py-2 rounded-lg font-medium transition-colors whitespace-nowrap
                    ${isActive ? 'bg-goszakup-50 text-goszakup-600 shadow-sm ring-1 ring-goszakup-100' : 'text-gray-600 hover:text-goszakup-600 hover:bg-gray-50'}`}
                >
                  {link.icon}
                  <span className={`text-sm ${link.path === '/news' ? '' : 'hidden 2xl:block'}`}>{link.name}</span>
                </Link>
              );
            })}
          </nav>
            
          <div className="flex shrink-0 items-center gap-1 sm:gap-2 ml-auto">

            {/* Balance widget — desktop only */}
            {isAuth && user && (
              <div className="relative hidden lg:block" ref={balanceRef}>
                <button
                  onClick={() => { setIsBalanceOpen(v => !v); setIsProfileMenuOpen(false); }}
                  aria-label={t('nav.balance')}
                  aria-expanded={isBalanceOpen}
                  aria-controls="balance-panel"
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                    isBalanceOpen ? 'bg-goszakup-100 text-goszakup-700 ring-1 ring-goszakup-200' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  {t('nav.balance')}
                  <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform duration-200 ${isBalanceOpen ? 'rotate-180' : ''}`} />
                </button>

                {isBalanceOpen && (
                  <div id="balance-panel" className="navigation-popover absolute right-0 mt-2 w-60 max-w-[calc(100vw-1rem)] bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-y-auto z-50">
                    {/* Subscription status */}
                    <div className="px-3 pt-3 pb-2 border-b border-gray-100">
                      <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider mb-1.5 px-1">{t('nav.subscription')}</p>
                      {subscription?.active ? (
                        <Link
                          to="/profile#subscription"
                          onClick={() => setIsBalanceOpen(false)}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-50 transition-colors group"
                        >
                          <Sparkles className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                          <span className="text-sm font-bold text-gray-800 truncate flex-1">{subscription.plan_name}</span>
                          <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full ${subscription.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                            {subscription.status === 'active' ? t('nav.subActive') : t('nav.subCancelled')}
                          </span>
                        </Link>
                      ) : (
                        <Link
                          to="/plans"
                          onClick={() => setIsBalanceOpen(false)}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-xl text-gray-400 hover:bg-slate-50 transition-colors"
                        >
                          <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="text-xs font-semibold flex-1">{t('nav.noSubscription')}</span>
                        </Link>
                      )}
                    </div>
                    <div className="px-4 pt-3 pb-1">
                      <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider">{t('nav.balance')}</p>
                    </div>
                    <div className="px-2 py-1.5 space-y-0.5">
                      {([
                        { label: t('nav.balancePfu'), value: user.balance?.pfu_calculations_left ?? 0, color: 'text-goszakup-600' },
                        { label: t('nav.balanceVip'), value: user.balance?.vip_calculations_left ?? 0, color: 'text-amber-600' },
                        { label: t('nav.balanceSlots'), value: user.balance?.tracker_slots ?? 0, color: 'text-emerald-600' },
                        { label: t('nav.balancePlanning'), value: user.balance?.planning_calculations_left ?? 0, color: 'text-violet-600' },
                        { label: t('nav.experience'), value: user.balance?.experience_credits ?? 0, color: 'text-goszakup-600' },
                        { label: t('nav.balanceTier'), value: user.balance?.tier_requirements_calculations_left ?? 0, color: 'text-indigo-600' },
                      ] as { label: string; value: number; color: string }[]).map((row, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
                          <span className="text-sm text-gray-600">{row.label}</span>
                          <span className={`text-sm font-extrabold tabular-nums ${row.color}`}>{row.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="px-3 pb-3 pt-1 space-y-1.5">
                      <Link
                        to="/profile#billing"
                        onClick={() => setIsBalanceOpen(false)}
                        className="flex items-center justify-center w-full py-2 rounded-xl bg-goszakup-600 text-white text-xs font-bold hover:bg-goszakup-700 transition-colors"
                      >
                        {t('nav.balanceFill')}
                      </Link>
                      <Link
                        to="/plans"
                        onClick={() => setIsBalanceOpen(false)}
                        className="flex items-center justify-center gap-1.5 w-full py-2 rounded-xl bg-amber-50 text-amber-700 text-xs font-bold hover:bg-amber-100 transition-colors"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        {subscription?.active ? t('nav.upgradePlan') : t('nav.viewPlans')}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isAuth ? (
              <div className="relative" ref={menuRef}>
                <button 
                  onClick={() => { setIsProfileMenuOpen(v => !v); setIsBalanceOpen(false); }}
                  aria-label={t('nav.profile')}
                  aria-expanded={isProfileMenuOpen}
                  aria-controls="profile-panel"
                  className={`flex min-h-[44px] items-center gap-2 px-2 py-1.5 rounded-lg font-bold transition-all whitespace-nowrap ${isProfileMenuOpen ? 'bg-goszakup-100 text-goszakup-700 ring-1 ring-goszakup-200' : 'text-goszakup-600 bg-goszakup-50 hover:bg-goszakup-100'}`}
                >
                  <div className="w-8 h-8 rounded-full bg-goszakup-600 text-white flex items-center justify-center font-extrabold text-sm">
                    {userInitials}
                  </div>
                  <span className="hidden xl:block text-sm">{t('nav.profile')}</span>
                </button>

                {/* Profile Dropdown Menu */}
                {isProfileMenuOpen && (
                  <div id="profile-panel" className="navigation-popover absolute right-0 mt-2 w-56 max-w-[calc(100vw-1rem)] bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-y-auto z-50">
                    {/* Balance summary — visible only on mobile (desktop has its own widget) */}
                    {user && (
                      <div className="lg:hidden px-4 pt-3 pb-2.5 border-b border-gray-100">
                        <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider mb-2">{t('nav.balance')}</p>
                        <div className="space-y-1.5">
                          {([
                            { label: t('nav.balancePfu'), value: user.balance?.pfu_calculations_left ?? 0, color: 'text-goszakup-600' },
                            { label: t('nav.balanceVip'), value: user.balance?.vip_calculations_left ?? 0, color: 'text-amber-600' },
                            { label: t('nav.balanceSlots'), value: user.balance?.tracker_slots ?? 0, color: 'text-emerald-600' },
                            { label: t('nav.balancePlanning'), value: user.balance?.planning_calculations_left ?? 0, color: 'text-violet-600' },
                            { label: t('nav.experience'), value: user.balance?.experience_credits ?? 0, color: 'text-goszakup-600' },
                            { label: t('nav.balanceTier'), value: user.balance?.tier_requirements_calculations_left ?? 0, color: 'text-indigo-600' },
                          ] as { label: string; value: number; color: string }[]).map((row, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">{row.label}</span>
                              <span className={`text-xs font-extrabold tabular-nums ${row.color}`}>{row.value}</span>
                            </div>
                          ))}
                        </div>
                        <Link
                          to="/profile#billing"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="mt-2 block text-center w-full py-1.5 rounded-lg bg-goszakup-50 text-goszakup-600 text-xs font-bold hover:bg-goszakup-100 transition-colors"
                        >
                          {t('nav.balanceFill')}
                        </Link>
                        <Link
                          to="/plans"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="mt-1.5 flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-bold hover:bg-amber-100 transition-colors"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          {subscription?.active ? `${subscription.plan_name}` : t('nav.viewPlans')}
                        </Link>
                      </div>
                    )}
                    <div className="p-2 flex flex-col gap-1">
                      <Link 
                        to="/profile" 
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 rounded-xl hover:bg-goszakup-50 hover:text-goszakup-700 transition-colors"
                      >
                        <User className="w-4 h-4 text-goszakup-500" />
                        {t('nav.goToProfile')}
                      </Link>
                      <button 
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 rounded-xl hover:bg-red-50 transition-colors text-left w-full"
                      >
                        <LogOut className="w-4 h-4 text-red-500" />
                        {t('nav.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link 
                to={authLink('/login', location.pathname + location.search + location.hash)}
                className={`flex min-h-[44px] items-center gap-2 px-3 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${location.pathname === '/login' ? 'bg-goszakup-100 text-goszakup-700 ring-1 ring-goszakup-200' : 'text-goszakup-600 bg-goszakup-50 hover:bg-goszakup-100'}`}
              >
                <span className="text-sm">{t('nav.login')}</span>
              </Link>
            )}

            <button 
              onClick={toggleLanguage} 
              aria-label={t('nav.switchLanguage')}
              className="flex min-h-[44px] items-center gap-1.5 px-2.5 py-2 bg-goszakup-600 text-white rounded-lg hover:bg-goszakup-700 transition-colors text-sm font-bold shadow-sm"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'ru' ? 'РУ' : 'ҚАЗ'}</span>
            </button>
          </div>
        </header>

        {/* Page Content Frame */}
        <main id="main-scroll" className="flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto relative outline-none w-full transition-all duration-300">
          <div className={`flex-1 min-w-0 flex flex-col w-full ${location.pathname === '/' ? '' : 'lg:pl-[6rem]'}`}>
            {children}
          </div>
          <Footer />
        </main>

      </div>
    </div>
  );
}
